/**
 * <shop-category-tiles> — progressive animation layer for the "Shop categories" section.
 *
 * Enhances tiles that opt in (via uploaded assets) with three behaviours; tiles with
 * only a static image are left exactly as server-rendered.
 *   1. Scroll parallax — `.shop-tile__layer` elements translate at individual speeds.
 *   2. Hover (pointer: fine) — crossfade the static stack into a looping <video>.
 *   3. Swipe slider (pointer: coarse / narrow) — the centred card auto-plays its loop.
 *
 * No Liquid in this file (it's a theme asset). Honours prefers-reduced-motion.
 */
class ShopCategoryTiles extends HTMLElement {
  connectedCallback() {
    this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.tiles = [...this.querySelectorAll('.shop-tile')];
    this.layers = [...this.querySelectorAll('.shop-tile__layer')];
    this.animated = [...this.querySelectorAll('[data-animated]')];
    this.coarse = matchMedia('(max-width: 1023px)'); // slider / touch range
    this.current = null; // the single tile whose video is playing

    this.layers.forEach((l) => l.style.setProperty('--speed', l.dataset.speed || 0));
    // guarantee inline muted playback regardless of how video_tag rendered the element
    this.querySelectorAll('.shop-tile__video').forEach((v) => {
      v.muted = true;
      v.setAttribute('playsinline', '');
      v.loop = true;
    });

    if (!this.reduceMotion) {
      this.#initParallax();
      this.#initHover();
      this.#initCentreAutoplay();
    }
    this.#initPrefetch();
  }

  /* Layer images are lazy, but tiles 2..n sit OFF-SCREEN in the horizontal slider, so
     the browser only starts fetching them mid-swipe — on cellular the user swipes onto a
     flat colour tile whose art pops in seconds later. Once the section scrolls anywhere
     near the viewport, promote every layer image to eager so all tiles are ready before
     the first swipe. */
  #initPrefetch() {
    const lazyImgs = [...this.querySelectorAll('img[loading="lazy"]')];
    if (!lazyImgs.length) return;
    this.prefetchIO = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        lazyImgs.forEach((img) => {
          img.loading = 'eager';
        });
        this.prefetchIO.disconnect();
        this.prefetchIO = null;
      },
      { rootMargin: '100% 0px' }
    );
    this.prefetchIO.observe(this);
  }

  disconnectedCallback() {
    this.#stopRaf?.();
    this.io?.disconnect();
    this.centreIO?.disconnect();
    this.prefetchIO?.disconnect();
  }

  /* ---- 1. scroll parallax ----
     Driven by a requestAnimationFrame loop (reading getBoundingClientRect), NOT the
     window 'scroll' event: Horizon's smooth/virtual scroll does not emit window scroll
     events, so a scroll listener never fires. The rAF loop runs ONLY while a tile is in
     view (IntersectionObserver gates it on/off) so it costs nothing off-screen.

     The rendered `--p` is EASED toward the scroll-derived target each frame (exponential
     smoothing) rather than snapped to it, so the layers glide instead of tracking the
     scrollbar 1:1 — a softer, more premium motion. It snaps (no glide) the first frame a
     tile enters view so it never visibly "catches up" on entry. */
  #initParallax() {
    if (!this.layers.length) return;
    const inView = new Set();
    let rafId = null;
    const EASE = 0.1; // per-frame approach rate toward target (~0.25s settle @60fps)

    const frame = () => {
      const vh = innerHeight;
      inView.forEach((tile) => {
        const r = tile.getBoundingClientRect();
        let target = (vh / 2 - (r.top + r.height / 2)) / (vh / 2);
        target = Math.max(-1, Math.min(1, target)); // clamp so travel stays within the headroom
        let cur = tile._p;
        if (cur == null) cur = target; // snap on first frame in view -> no entry glide
        cur += (target - cur) * EASE;
        if (Math.abs(target - cur) < 0.0005) cur = target; // settle exactly, kill float drift
        tile._p = cur;
        tile.style.setProperty('--p', cur.toFixed(4));
      });
      rafId = inView.size ? requestAnimationFrame(frame) : null;
    };
    const start = () => {
      if (rafId == null) rafId = requestAnimationFrame(frame);
    };

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            inView.add(e.target);
          } else {
            inView.delete(e.target);
            e.target._p = null; // forget -> snaps (not glides) when it scrolls back into view
          }
        });
        if (inView.size) start();
      },
      { threshold: 0 }
    );
    this.tiles.forEach((t) => this.io.observe(t));
    this.#stopRaf = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }

  #stopRaf;

  /* ---- video helpers (lazy load, single concurrent playback) ---- */
  #play(tile) {
    const v = tile.querySelector('.shop-tile__video');
    if (!v) return;
    if (this.current && this.current !== tile) this.#stop(this.current);
    this.current = tile;
    if (!v.dataset.loaded) {
      v.dataset.loaded = '1';
      v.load();
      // Crossfade only once decoded video frames actually exist. Flagging is-playing on
      // intent (before playback starts) paints an empty <video> at opacity:1 over the art
      // while the file buffers — on a slow connection the tile looks blank/broken for the
      // whole download. videoWidth===0 also catches streams iOS can't render (e.g. a
      // source it accepted but decodes audio-only): those keep the static art forever.
      v.addEventListener('playing', () => {
        if (v.videoWidth > 0 && this.current === tile && !v.paused) {
          tile.classList.add('is-playing');
        }
      });
    }
    const r = v.play();
    // Autoplay can be refused (iOS Low Power Mode, data saver). Fall back to the static
    // stack instead of leaving an is-playing tile whose empty <video> sits at opacity:1 —
    // on iOS that paints a "can't play" glyph over the art.
    if (r && r.catch) r.catch(() => this.#stop(tile));
  }

  #stop(tile) {
    const v = tile.querySelector('.shop-tile__video');
    if (!v) return;
    tile.classList.remove('is-playing');
    v.pause();
    if (this.current === tile) this.current = null;
  }

  /* ---- 2. desktop hover ---- */
  #initHover() {
    this.animated.forEach((tile) => {
      tile.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'touch' && !this.coarse.matches) this.#play(tile);
      });
      tile.addEventListener('pointerleave', () => {
        if (!this.coarse.matches) this.#stop(tile);
      });
    });
  }

  /* ---- 3. slider: play the centred card, pause the rest ---- */
  #initCentreAutoplay() {
    if (!this.animated.length) return;
    this.centreIO = new IntersectionObserver(
      (es) => {
        if (!this.coarse.matches) return;
        es.forEach((e) => {
          if (!e.target.matches('[data-animated]')) return;
          // Ratio of the visible part to what COULD be visible: a slider card can
          // be taller than the viewport (tablet), where intersectionRatio alone
          // never reaches a high threshold.
          const r = e.boundingClientRect;
          const vis = e.intersectionRect;
          const visX = vis.width / Math.min(r.width, innerWidth || 1);
          const visY = vis.height / Math.min(r.height, innerHeight || 1);
          visX > 0.75 && visY > 0.75 ? this.#play(e.target) : this.#stop(e.target);
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    this.tiles.forEach((t) => this.centreIO.observe(t));
    // when flipping back to desktop, stop any slider-triggered playback
    this.coarse.addEventListener('change', () => this.tiles.forEach((t) => this.#stop(t)));
  }
}

if (!customElements.get('shop-category-tiles')) {
  customElements.define('shop-category-tiles', ShopCategoryTiles);
}
